import { useEffect, useMemo, useState } from "react";
import type { CartResponse } from "@/features/cart/types/cart.type";

const TICK_RATE_MS = 1000;

const formatRemaining = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return [
      hours.toString().padStart(2, "0"),
      minutes.toString().padStart(2, "0"),
      seconds.toString().padStart(2, "0"),
    ].join(":");
  }

  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

export const useCartReservationTimer = (cart?: CartResponse) => {
  const expiresAt = useMemo(() => {
    if (!cart) return null;
    if (cart.reservation_expires_at instanceof Date) return cart.reservation_expires_at;

    if (cart.reserved_until) {
      const fromReservedUntil = new Date(cart.reserved_until);
      if (!Number.isNaN(fromReservedUntil.getTime())) {
        return fromReservedUntil;
      }
    }

    if (typeof cart?.ttl === "number" && Number.isFinite(cart.ttl)) {
      const fromTtl = new Date(cart.ttl * 1000);
      if (!Number.isNaN(fromTtl.getTime())) {
        return fromTtl;
      }
    }

    return null;
  }, [cart]);

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAt) return undefined;

    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), TICK_RATE_MS);
    return () => window.clearInterval(interval);
  }, [expiresAt]);

  const remainingMs = expiresAt ? expiresAt.getTime() - now : 0;
  const isExpired = remainingMs <= 0;
  const safeRemainingMs = Math.max(remainingMs, 0);

  return {
    expiresAt,
    remainingMs: safeRemainingMs,
    formatted: expiresAt ? formatRemaining(safeRemainingMs) : null,
    isExpired,
    isExpiringSoon: safeRemainingMs <= 5 * 60 * 1000 && !isExpired,
  };
};

export type CartReservationTimer = ReturnType<typeof useCartReservationTimer>;
